import * as React from 'react';

import { DescriptionPage_ProductVariant as ProductVariant } from '__generated__/graphql-types';

import ReviewsRatingCard from 'bundles/reviews-common/components/cds/ReviewsRatingCard';
import ReviewsBackButton from 'bundles/reviews/components/ReviewsBackButton';
import ReviewsPageLayout from 'bundles/reviews/components/ReviewsPageLayout';
import ReviewsProductCard from 'bundles/reviews/components/ReviewsProductCard';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import type { SpecializationReviewsPageData } from 'bundles/reviews/hooks/useSpecializationReviewsPageQuery';
import { getFormattedCoursesForEnrollButton } from 'bundles/reviews/utils/getFormattedCoursesForEnrollButton';
import ConsumerEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerEnrollButtonNotConfigurable';
import { useSpecializationCourseFilter } from 'bundles/unified-description-page-common/hooks/useSpecializationCourseFilter';
import {
  getConsumerRouteForSpecializationProducts,
  getEnterpriseRouteForSpecializationProducts,
} from 'bundles/unified-description-page-common/utils/getRouteForSpecializationProducts';

import _t from 'i18n!nls/reviews';

const PROFESSIONAL_CERTIFICATE_VARIANTS: ProductVariant[] = [
  ProductVariant.ProfessionalCertificate,
  ProductVariant.ExternalCertificate,
  ProductVariant.GoogleCertificate,
];

type Props = {
  reviewsPageData: SpecializationReviewsPageData;
  productVariant: ProductVariant;
  slug: string;
  programSlug?: string;
  orgSlug?: string;
};

const SpecializationReviewsPageContent = ({ reviewsPageData, productVariant, slug, programSlug, orgSlug }: Props) => {
  const isProfessionalCertificate = PROFESSIONAL_CERTIFICATE_VARIANTS.includes(productVariant);
  const productTypeAttributes = reviewsPageData.productCard?.productTypeAttributes;
  const specializationCanonical =
    productTypeAttributes?.__typename === 'ProductCard_Specialization' ? productTypeAttributes.canonical : null;
  const cardImageUrl = specializationCanonical?.cardImageUrl ?? null;
  const courses = specializationCanonical?.courses ?? [];
  const { courseFilterOptions, selectedCourseSlug, setSelectedCourseSlug, selectedCourseId } =
    useSpecializationCourseFilter(courses);

  const courseNameById = new Map(courseFilterOptions.map((course) => [course.id, course.name]));

  const topReviews = (reviewsPageData.topReviews ?? [])
    .filter((topReview): topReview is NonNullable<typeof topReview> => topReview != null)
    .map(({ courseId, review }) => ({ ...review, courseName: courseNameById.get(courseId) }));

  const backHref =
    orgSlug || programSlug
      ? getEnterpriseRouteForSpecializationProducts(productVariant, slug, programSlug, undefined, orgSlug)
      : getConsumerRouteForSpecializationProducts(productVariant, slug);

  const partner = reviewsPageData.partners?.[0];

  // Enterprise/org-scoped reviews pages show no enrollment CTA -- an enterprise learner enrolls
  // through their program, and EnterpriseSpecializationEnrollButtonNotConfigurable relies on
  // context hooks (program/org membership, etc.) that assume it's mounted inside the full XDP
  // provider tree, which this standalone page doesn't have.
  const enrollmentCta =
    orgSlug || programSlug ? null : (
      <ConsumerEnrollButtonNotConfigurable
        s12nId={reviewsPageData.id}
        courseCount={courses.length}
        partnerIds={reviewsPageData.partners?.map((p) => p.id)}
        name={reviewsPageData.name}
        slug={reviewsPageData.slug}
        isSpecialization
        s12nCourses={getFormattedCoursesForEnrollButton(courses)}
        s12nProductVariant={productVariant}
      />
    );

  return (
    <ReviewsPageLayout
      backButton={<ReviewsBackButton courseName={reviewsPageData.name} href={backHref} />}
      productCard={
        <ReviewsProductCard
          courseId={reviewsPageData.id}
          courseName={reviewsPageData.name}
          description={reviewsPageData.description}
          difficultyLevel={reviewsPageData.difficultyLevel}
          enrollmentCta={enrollmentCta}
          imageUrl={cardImageUrl}
          marketingProductType={isProfessionalCertificate ? 'PROFESSIONAL_CERTIFICATE' : 'SPECIALIZATION'}
          partnerLogoUrl={partner?.squareLogo ?? partner?.logo}
          partnerName={partner?.name}
          programSlug={programSlug}
          skillTags={(reviewsPageData.skillTags ?? []).map((tag) => tag.name)}
          totalDuration={reviewsPageData.material?.totalDuration}
        />
      }
      ratingCard={
        <ReviewsRatingCard
          averageFiveStarRating={reviewsPageData.ratings?.averageFiveStarRating}
          ratingCountsByStar={reviewsPageData.ratingCountsByStar}
          numberOfCourses={courses.length}
        />
      }
    >
      <TopReviewsSection
        title={_t('Top reviews across #{name}', { name: reviewsPageData.name })}
        reviews={topReviews.map((review) => ({ key: review.id, ...review }))}
      />
      {/* ProductReviewsV1Resource.byProduct's productId is a union field -- courses must be
          passed as a "COURSE~<id>" stringkey, not the bare course id. */}
      {selectedCourseId ? (
        <ReviewsTable
          courseId={`COURSE~${selectedCourseId}`}
          courseFilterOptions={courseFilterOptions}
          selectedCourseSlug={selectedCourseSlug}
          onCourseChange={setSelectedCourseSlug}
          specializationName={reviewsPageData.name}
        />
      ) : null}
    </ReviewsPageLayout>
  );
};

export default SpecializationReviewsPageContent;
